import React, { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { HiArrowLeft, HiSignal, HiLightBulb, HiServer, HiStar, HiArrowTrendingUp, HiCursorArrowRays, HiShieldCheck } from 'react-icons/hi2'
import Plot from 'react-plotly.js'
import { fetchGamePrediction, fetchCompleteGameDetails, fetchRedditComments, fetchPredictionParameters } from '../services/api'
import ConfidenceIndicator from '../components/ConfidenceIndicator'
import PredictionParameters from '../components/PredictionParameters'

interface SentimentData {
  timestamp: string
  sentiment_score: number
  sentiment_label: string
  total_tweets: number
}

interface PredictionData {
  game_id: string
  home_team: string
  away_team: string
  home_confidence: number
  away_confidence: number
  predicted_winner: string
  prediction_strength: string
  data_sources: {
    reddit_comments: number
    twitter_tweets: number
    total_sentiment_data: number
  }
  fan_breakdown: {
    home_fans: number
    away_fans: number
    neutral_fans: number
  }
  reliability_metrics?: {
    overall_confidence: number
    overall_reliability: string
    home_team_reliability: {
      confidence_score: number
      reliability_tier: string
      sample_size: number
    }
    away_team_reliability: {
      confidence_score: number
      reliability_tier: string
      sample_size: number
    }
    data_quality_breakdown: {
      reddit_count: number
      twitter_count: number
      total_count: number
    }
  }
  metrics?: {
    average?: { home: number; away: number; difference: number }
    cumulative?: { home: number; away: number; difference: number }
    composite?: { home: number; away: number; difference: number }
  }
}

interface GameDetails {
  game_id: string
  status: string
  home_team: string
  away_team: string
  home_team_hashtag: string
  away_team_hashtag: string
  start_time: string
  kickoff_time: string | null
  home_score: number | null
  away_score: number | null
  predicted_winner: string | null
  home_confidence_score: number | null
  away_confidence_score: number | null
  neutral_sentiment: number | null
  last_analyzed_at: string | null
  created_at: string
  updated_at: string
}

interface ClubDetails {
  club_id: string
  club_name: string
  subreddit: string
  subreddit_members: number | null
  hashtag: string | null
  sentiment_bias: number | null
  real_home_bias: number | null
  real_away_bias: number | null
  bias_last_calculated: string | null
  bias_sample_size: number | null
  created_at: string
  updated_at: string
}

interface RedditCommentsSummary {
  total_count: number
  summary: {
    home_fans: number
    away_fans: number
    neutral_fans: number
    avg_sentiment_score: number
  }
}

type TeamFan = 'HOME_FAN' | 'AWAY_FAN' | 'NEUTRAL' | null

interface RedditComment {
  comment_id: string
  comment_text: string
  author_username: string
  raw_score?: number | null
  sentiment_score: number
  sentiment_label: string
  normalized_score?: number | null
  relevance_score?: number | null
  is_team_fan?: TeamFan
  created_at_comment: string
  upvotes?: number | null
  downvotes?: number | null
}

interface RedditBucketPoint {
  timestamp: string
  weighted_sum: number
  count: number
  avg: number
  home_weighted_sum: number
  away_weighted_sum: number
  net_home_minus_away: number
}

interface CompleteGameData {
  game: GameDetails
  home_club: ClubDetails | null
  away_club: ClubDetails | null
  reddit_comments: RedditCommentsSummary
}

// Helper function to format dates in user's timezone
const formatDateWithTimezone = (dateString: string | null): string => {
  if (!dateString) return 'Date/time unavailable'
  
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return 'Invalid date'
    
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const timeZoneName = Intl.DateTimeFormat('en', { 
      timeZoneName: 'short',
      timeZone 
    }).formatToParts(date).find(part => part.type === 'timeZoneName')?.value || ''
    
    const dateStr = date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone
    })
    
    const timeStr = date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone
    })
    
    return `${dateStr} • ${timeStr} ${timeZoneName}`
  } catch (error) {
    console.error('Error formatting date:', error)
    return 'Date/time unavailable'
  }
}

const LiveGamePage: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>()
  const [sentimentData, _setSentimentData] = useState<SentimentData[]>([])
  const [predictionData, setPredictionData] = useState<PredictionData | null>(null)
  const [completeGameData, setCompleteGameData] = useState<CompleteGameData | null>(null)
  const [redditComments, setRedditComments] = useState<RedditComment[] | null>(null)
  const [redditSeries, setRedditSeries] = useState<RedditBucketPoint[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [predictionParams, setPredictionParams] = useState<{
    parameters: {
      draw_threshold: number
      shrinkage_k: number
      normalization_scale: number
      composite_weight: number
      optimization_accuracy: number | null
      optimized_at: string | null
    }
    isOptimized: boolean
  } | null>(null)
  
  const plotRef = useRef<Plot>(null)

  useEffect(() => {
    if (gameId) {
      loadGameData()
    }
  }, [gameId])

  // Poll for live updates every 30 seconds
  useEffect(() => {
    if (!gameId) return
    
    const interval = setInterval(() => {
      loadGameData()
    }, 30000) // Update every 30 seconds
    
    return () => clearInterval(interval)
  }, [gameId])

  const loadGameData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Load complete game data first (required)
      await loadCompleteGameData()
      
      // Load optional data in parallel
      await Promise.allSettled([
        loadPredictionData(),
        loadRedditData(),
        loadPredictionParameters()
      ])
    } catch (err) {
      setError('Failed to load game data')
      console.error('Error loading game data:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadRedditData = async () => {
    try {
      if (!gameId) return
      const res = await fetchRedditComments(gameId, 1000)
      const comments: RedditComment[] = (res?.comments || []).map((c: any) => ({
        comment_id: c.comment_id,
        comment_text: c.comment_text,
        author_username: c.author_username,
        raw_score: c.raw_score ?? null,
        sentiment_score: Number(c.sentiment_score ?? 0),
        sentiment_label: c.sentiment_label,
        normalized_score: c.normalized_score ?? null,
        relevance_score: c.relevance_score ?? null,
        is_team_fan: (c.is_team_fan ?? null) as TeamFan,
        created_at_comment: c.created_at_comment,
        upvotes: c.upvotes ?? null,
        downvotes: c.downvotes ?? null
      }))
      setRedditComments(comments)
      const series = buildRedditTimeSeries(comments, 5) // 5-minute buckets for live data
      setRedditSeries(series)
    } catch (e) {
      console.warn('Failed to load reddit comments timeline', e)
    }
  }

  const buildRedditTimeSeries = (comments: RedditComment[], bucketMinutes = 5): RedditBucketPoint[] => {
    if (!comments || comments.length === 0) return []

    const buckets = new Map<number, {
      sum: number
      count: number
      homeSum: number
      awaySum: number
    }>()

    const msPerBucket = bucketMinutes * 60 * 1000

    for (const c of comments) {
      const t = new Date(c.created_at_comment).getTime()
      if (isNaN(t)) continue
      const bucketTs = Math.floor(t / msPerBucket) * msPerBucket
      let base = Number.isFinite(Number(c.raw_score)) ? Number(c.raw_score) : NaN
      if (!Number.isFinite(base)) {
        const mag = Number.isFinite(Number(c.normalized_score)) ? Number(c.normalized_score) : Number(c.sentiment_score ?? 0)
        const sign = c.sentiment_label === 'negative' ? -1 : c.sentiment_label === 'positive' ? 1 : 0
        base = mag * sign
      }
      const relevance = Number(c.relevance_score ?? 1)
      const contribution = (Number.isFinite(base) ? base : 0) * (Number.isFinite(relevance) ? relevance : 1)
      const current = buckets.get(bucketTs) || { sum: 0, count: 0, homeSum: 0, awaySum: 0 }
      current.sum += contribution
      current.count += 1
      if (c.is_team_fan === 'HOME_FAN') current.homeSum += contribution
      if (c.is_team_fan === 'AWAY_FAN') current.awaySum += contribution
      buckets.set(bucketTs, current)
    }

    const points = Array.from(buckets.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([ts, v]) => ({
        timestamp: new Date(ts).toISOString(),
        weighted_sum: v.sum,
        count: v.count,
        avg: v.count > 0 ? v.sum / v.count : 0,
        home_weighted_sum: v.homeSum,
        away_weighted_sum: v.awaySum,
        net_home_minus_away: v.homeSum - v.awaySum
      }))

    return points
  }

  const loadCompleteGameData = async () => {
    try {
      console.log('Loading complete game data for:', gameId)
      const data = await fetchCompleteGameDetails(gameId!)
      console.log('Complete game data loaded:', data)
      setCompleteGameData(data)
    } catch (err) {
      console.error('Error loading complete game data:', err)
      throw err
    }
  }

  const loadPredictionData = async () => {
    try {
      console.log('Loading prediction data for:', gameId)
      const data = await fetchGamePrediction(gameId!)
      console.log('Prediction data loaded:', data)
      setPredictionData(data)
    } catch (err) {
      console.warn('Failed to load prediction data:', err)
    }
  }

  const loadPredictionParameters = async () => {
    try {
      const data = await fetchPredictionParameters()
      if (data && data.success) {
        setPredictionParams({
          parameters: data.parameters,
          isOptimized: data.is_optimized
        })
      }
    } catch (err) {
      console.warn('Failed to load prediction parameters:', err)
      // Don't set error for prediction parameters as it's optional
    }
  }

  // Helper function to calculate sentiment label distribution
  const getSentimentDistribution = () => {
    if (!redditComments || redditComments.length === 0) {
      return { positive: 0, negative: 0, neutral: 0 }
    }
    return {
      positive: redditComments.filter(c => c.sentiment_label === 'positive').length,
      negative: redditComments.filter(c => c.sentiment_label === 'negative').length,
      neutral: redditComments.filter(c => c.sentiment_label === 'neutral' || !c.sentiment_label).length
    }
  }

  // Helper function to calculate relevance metrics
  const getRelevanceMetrics = () => {
    if (!redditComments || redditComments.length === 0) {
      return { avgRelevance: 0, effectiveSample: 0, avgUpvotes: 0, totalComments: 0 }
    }
    const relevanceScores = redditComments
      .map(c => c.relevance_score ?? 1.0)
      .filter(score => score !== null && score !== undefined)
    const upvotes = redditComments
      .map(c => c.upvotes ?? 0)
      .filter(votes => votes !== null && votes !== undefined)
    
    const avgRelevance = relevanceScores.length > 0 
      ? relevanceScores.reduce((sum, score) => sum + score, 0) / relevanceScores.length 
      : 0
    const effectiveSample = relevanceScores.reduce((sum, score) => sum + score, 0)
    const avgUpvotes = upvotes.length > 0
      ? upvotes.reduce((sum, votes) => sum + votes, 0) / upvotes.length
      : 0
    
    return {
      avgRelevance: avgRelevance,
      effectiveSample: effectiveSample,
      avgUpvotes: avgUpvotes,
      totalComments: redditComments.length
    }
  }

  const sentimentDistribution = getSentimentDistribution()
  const relevanceMetrics = getRelevanceMetrics()

  // Prepare plot data (prefer Reddit timeline)
  const plotData = redditSeries && redditSeries.length > 0
    ? [
        {
          x: redditSeries.map(d => d.timestamp),
          y: redditSeries.map(d => d.home_weighted_sum),
          type: 'scatter' as const,
          mode: 'lines' as const,
          name: 'Home Fans',
          line: { color: '#FF7700', width: 1.5 }
        },
        {
          x: redditSeries.map(d => d.timestamp),
          y: redditSeries.map(d => d.away_weighted_sum),
          type: 'scatter' as const,
          mode: 'lines' as const,
          name: 'Away Fans',
          line: { color: '#ffffff', width: 1.5 }
        }
      ]
    : sentimentData.length > 0
    ? [
    {
      x: sentimentData.map(d => d.timestamp),
      y: sentimentData.map(d => d.sentiment_score),
      type: 'scatter' as const,
      mode: 'lines+markers' as const,
      name: 'Sentiment Score',
      line: {
        color: sentimentData.map(d => 
          d.sentiment_score > 0 ? '#FF7700' : 
          d.sentiment_score < 0 ? '#FF7700' : '#6b7280'
        ),
        width: 3
      },
      marker: {
        size: 8,
        color: sentimentData.map(d => 
          d.sentiment_score > 0 ? '#FF7700' : 
          d.sentiment_score < 0 ? '#FF7700' : '#6b7280'
        )
      }
    }
  ]
    : []

  const plotLayout = {
    title: {
      text: `Live Sentiment Analysis - ${completeGameData?.game?.home_team || completeGameData?.game?.home_team_hashtag} vs ${completeGameData?.game?.away_team || completeGameData?.game?.away_team_hashtag}`,
      font: { size: 18, color: '#ffffff' }
    },
    xaxis: {
      title: 'Time',
      type: 'date' as const
    },
    yaxis: {
      title: 'Sentiment Score',
      zeroline: true,
      zerolinecolor: '#6b7280'
    },
    showlegend: true,
    legend: {
      x: 0,
      y: 1
    },
    margin: { t: 60, r: 30, b: 50, l: 50 },
    paper_bgcolor: 'black',
    plot_bgcolor: 'black'
  }

  const plotConfig = {
    displayModeBar: true,
    displaylogo: false
  }

  if (loading && !completeGameData) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error && !completeGameData) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{error}</div>
        <button 
          onClick={loadGameData}
          className="btn-primary"
        >
          Try Again
        </button>
      </div>
    )
  }

  if (!completeGameData) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 mb-4">Game not found</div>
        <Link to="/" className="btn-primary">
          Back to Games
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link 
          to="/" 
          className="flex items-center space-x-2 text-gray-300 hover:text-orange-400 transition-colors"
        >
          <HiArrowLeft className="h-4 w-4" />
          <span>Back to Games</span>
        </Link>
      </div>
        
      {/* Game Info - Live Header */}
      {completeGameData && (
        <div className="card">
          <div className="text-center">
            <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">
              {completeGameData.game.home_team || completeGameData.game.home_team_hashtag} vs {completeGameData.game.away_team || completeGameData.game.away_team_hashtag}
            </h1>
            <div className="flex items-center justify-center space-x-2 mb-2">
              <HiSignal className="h-4 w-4 text-orange-500 animate-pulse" />
              <span className="live-indicator">LIVE</span>
            </div>
            {(completeGameData.game.home_score !== null && completeGameData.game.away_score !== null) && (
              <div className="text-2xl sm:text-3xl font-bold text-white mt-4">
                {completeGameData.game.home_score} - {completeGameData.game.away_score}
              </div>
            )}
            {completeGameData.game.last_analyzed_at && (
              <div className="flex items-center justify-center space-x-2 mt-2">
                <HiServer className="h-4 w-4 text-orange-400" />
                <div className="text-[11px] sm:text-xs text-gray-400">
                  Last updated: {formatDateWithTimezone(completeGameData.game.last_analyzed_at)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}


      {/* Live Sentiment Timeline */}
      <div className="card">
        <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">
          Pre-Match Sentiment Timeline
        </h2>
        <div className="h-96">
          {plotData.length > 0 ? (
            <Plot
              ref={plotRef}
              data={plotData}
              layout={plotLayout}
              config={plotConfig}
              style={{ width: '100%', height: '100%' }}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              <div className="text-center">
                <div className="text-gray-500 mb-4">
                  Waiting for sentiment data...
                </div>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Step-by-Step Analysis Pipeline */}
      {predictionData && (
        <div className="space-y-6">
          {/* Stage 1: Data Collection */}
          <div className="card">
            <div className="flex items-center mb-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-600 text-white font-bold text-lg mr-4">
                1
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-white flex items-center">
                <HiServer className="h-6 w-6 mr-2 text-orange-600" />
                Stage 1: Data Collection
              </h2>
            </div>
            <div className="space-y-3 text-gray-300">
              <div>
                <p className="font-medium mb-1">What:</p>
                <p className="text-sm">Reddit comments from both teams subreddits were collected from the last 72 hours before the match.</p>
              </div>
              <div>
                <p className="font-medium mb-1">Why:</p>
                <p className="text-sm">We need pre-match fan sentiment data to understand expectations before the game begins.</p>
              </div>
              <div className="bg-gray-800 p-4 rounded-lg mt-4">
                <p className="text-sm font-medium mb-2">Comment Counts for This Game:</p>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Home Fans:</span>
                    <div className="text-xl font-bold text-orange-400">{completeGameData?.reddit_comments?.summary?.home_fans || 0}</div>
                  </div>
                  <div>
                    <span className="text-gray-400">Away Fans:</span>
                    <div className="text-xl font-bold text-orange-400">{completeGameData?.reddit_comments?.summary?.away_fans || 0}</div>
                  </div>
                  <div>
                    <span className="text-gray-400">Total:</span>
                    <div className="text-xl font-bold text-white">{completeGameData?.reddit_comments?.total_count || 0}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stage 2: Sentiment Analysis */}
          <div className="card">
            <div className="flex items-center mb-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-600 text-white font-bold text-lg mr-4">
                2
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-white flex items-center">
                <HiStar className="h-6 w-6 mr-2 text-orange-600" />
                Stage 2: Sentiment Analysis
              </h2>
            </div>
            <div className="space-y-3 text-gray-300">
              <div>
                <p className="font-medium mb-1">What:</p>
                <p className="text-sm">Each comment is analyzed by a RoBERTa model that understands football slang and team-specific vocabulary.</p>
              </div>
              <div>
                <p className="font-medium mb-1">Why:</p>
                <p className="text-sm">We need to convert text comments into numeric sentiment scores so we can calculate confidence levels mathematically.</p>
              </div>
              <div>
                <p className="font-medium mb-1">How we refine it:</p>
                <p className="text-sm">We cross-check nuanced terms against our <code>Loughran-McDonald_MasterDictionary_1993-2024.csv</code> lexicon so tricky fan language (injury chatter, momentum swings, sarcasm triggers) gets sentiment adjustments beyond the base model.</p>
              </div>
              <div className="bg-gray-800 p-4 rounded-lg mt-4">
                <p className="text-sm font-medium mb-2">Sentiment Label Mapping:</p>
                <pre className="text-xs bg-gray-900 p-3 rounded border border-gray-700 overflow-x-auto"><code>{`label -> score
positive = +confidence
negative = -confidence
neutral  =  0.0
range    = [-1, +1]`}</code></pre>
              </div>
              <div className="bg-gray-800 p-4 rounded-lg mt-4">
                <p className="text-sm font-medium mb-2">Sentiment Distribution for This Game (from actual comments):</p>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <span className="text-gray-400 block mb-1">Positive</span>
                    <div className="text-2xl font-bold text-orange-400">{sentimentDistribution.positive}</div>
                  </div>
                  <div className="text-center">
                    <span className="text-gray-400 block mb-1">Negative</span>
                    <div className="text-2xl font-bold text-orange-400">{sentimentDistribution.negative}</div>
                  </div>
                  <div className="text-center">
                    <span className="text-gray-400 block mb-1">Neutral</span>
                    <div className="text-2xl font-bold text-gray-400">{sentimentDistribution.neutral}</div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Counted from {redditComments?.length || 0} comments for this game
                </p>
              </div>
            </div>
          </div>

          {/* Stage 3: Bias Normalization */}
          <div className="card">
            <div className="flex items-center mb-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-600 text-white font-bold text-lg mr-4">
                3
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-white flex items-center">
                <HiArrowTrendingUp className="h-6 w-6 mr-2 text-orange-600" />
                Stage 3: Bias Normalization
              </h2>
            </div>
            <div className="space-y-3 text-gray-300">
              <div>
                <p className="font-medium mb-1">What:</p>
                <p className="text-sm">Each team's baseline sentiment bias from their general subreddit (7 days pre-match) is used to adjust raw sentiment scores.</p>
              </div>
              <div>
                <p className="font-medium mb-1">Why:</p>
                <p className="text-sm">Some fanbases are naturally more optimistic or pessimistic. By removing this inherent bias, we get the true match-specific sentiment.</p>
              </div>
              <div className="bg-gray-800 p-4 rounded-lg mt-4">
                <p className="text-sm font-medium mb-2">Formula:</p>
                <pre className="text-xs bg-gray-900 p-3 rounded border border-gray-700 overflow-x-auto"><code>{`normalized_score = raw_score - bias
# bias: mean baseline sentiment from general fan subreddit (7 days pre-match)`}</code></pre>
              </div>
              <div className="bg-gray-800 p-4 rounded-lg mt-4">
                <p className="text-sm font-medium mb-2">7-Day Bias Values for This Game:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">{completeGameData?.home_club?.club_name || 'Home Team'} 7-Day Bias:</span>
                    <div className="text-xl font-bold text-orange-400">{(completeGameData?.home_club?.real_home_bias ?? 0).toFixed(3)}</div>
                  </div>
                  <div>
                    <span className="text-gray-400">{completeGameData?.away_club?.club_name || 'Away Team'} 7-Day Bias:</span>
                    <div className="text-xl font-bold text-orange-400">{(completeGameData?.away_club?.real_away_bias ?? 0).toFixed(3)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stage 4: Confidence Calculations */}
      <div className="card">
            <div className="flex items-center mb-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-600 text-white font-bold text-lg mr-4">
                4
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-white flex items-center">
                <HiLightBulb className="h-6 w-6 mr-2 text-orange-600" />
                Stage 4: Confidence Calculations
        </h2>
            </div>
            
            {/* Step 4.1: Average Confidence */}
            <div className="bg-gray-800 p-4 rounded-lg mb-4">
              <h3 className="text-lg font-medium text-white mb-3">Step 4.1: Average Confidence</h3>
              <div className="space-y-2 text-gray-300">
                <div>
                  <p className="font-medium mb-1">What:</p>
                  <p className="text-sm">Weighted average of normalized sentiment scores across all comments.</p>
                </div>
                <div>
                  <p className="font-medium mb-1">Why:</p>
                  <p className="text-sm">Represents the typical fan sentiment level, giving equal importance to each comment's relevance.</p>
                </div>
                <div className="mt-3">
                  <p className="text-sm font-medium mb-1">Formula:</p>
                  <pre className="text-xs bg-gray-900 p-2 rounded border border-gray-700 overflow-x-auto"><code>{`avg_confidence = Σ(normalized_score × relevance) / Σ(relevance)`}</code></pre>
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-400 text-sm">Home Average:</span>
                    <div className="text-xl font-bold text-orange-400">{(predictionData.metrics?.average?.home ?? predictionData.home_confidence).toFixed(3)}</div>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">Away Average:</span>
                    <div className="text-xl font-bold text-orange-400">{(predictionData.metrics?.average?.away ?? predictionData.away_confidence).toFixed(3)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4.2: Cumulative Confidence */}
            <div className="bg-gray-800 p-4 rounded-lg mb-4">
              <h3 className="text-lg font-medium text-white mb-3">Step 4.2: Cumulative Confidence</h3>
              <div className="space-y-2 text-gray-300">
                <div>
                  <p className="font-medium mb-1">What:</p>
                  <p className="text-sm">Sum of all weighted positive and negative sentiment values.</p>
                </div>
                <div>
                  <p className="font-medium mb-1">Why:</p>
                  <p className="text-sm">Captures both volume and intensity of sentiment - a few very passionate comments can outweigh many neutral ones.</p>
                </div>
                <div className="mt-3">
                  <p className="text-sm font-medium mb-1">Formula:</p>
                  <pre className="text-xs bg-gray-900 p-2 rounded border border-gray-700 overflow-x-auto"><code>{`weighted = normalized_score × relevance_score
positive_sum = Σ(max(weighted, 0))
negative_sum = |Σ(min(weighted, 0))|
cumulative_confidence = positive_sum - negative_sum`}</code></pre>
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-400 text-sm">Home Cumulative:</span>
                    <div className="text-xl font-bold text-orange-400">{(predictionData.metrics?.cumulative?.home ?? predictionData.home_confidence).toFixed(3)}</div>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">Away Cumulative:</span>
                    <div className="text-xl font-bold text-orange-400">{(predictionData.metrics?.cumulative?.away ?? predictionData.away_confidence).toFixed(3)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4.3: Composite Score */}
            <div className="bg-gray-800 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-white mb-3">Step 4.3: Composite Score</h3>
              <div className="space-y-2 text-gray-300">
                <div>
                  <p className="font-medium mb-1">What:</p>
                  <p className="text-sm">Combines average and cumulative confidence, adjusted for sample size reliability.</p>
                </div>
                <div>
                  <p className="font-medium mb-1">Why:</p>
                  <p className="text-sm">The final balanced metric that considers both typical sentiment (average) and passionate intensity (cumulative), weighted by how reliable our sample is.</p>
                </div>
                <div className="mt-3">
                  <p className="text-sm font-medium mb-1">Formula:</p>
                  <pre className="text-xs bg-gray-900 p-2 rounded border border-gray-700 overflow-x-auto"><code>{`effective_sample = Σ(relevance_score)
sample_reliability = log(effective_sample + 1) / log(1001)
shrunken_avg = (effective_sample / (effective_sample + ${predictionParams?.parameters?.shrinkage_k ?? 30})) × avg_confidence
normalized_cum = tanh(cumulative_confidence / ${predictionParams?.parameters?.normalization_scale ?? 25})
composite = shrunken_avg + ${predictionParams?.parameters?.composite_weight ?? 0.25} × normalized_cum × sample_reliability`}</code></pre>
                </div>
                <div className="mt-3 p-3 bg-gray-900 rounded border border-gray-700">
                  <p className="text-xs font-medium mb-2 text-gray-300">Key Definitions:</p>
                  <ul className="text-xs text-gray-400 space-y-1 list-disc list-inside">
                    <li><strong>relevance_score</strong> (0.0-1.0): Weighted combination of quality_score (30%), engagement_score (30%), and football_relevance (40%). Average for this game: <span className="text-orange-400 font-semibold">{relevanceMetrics.avgRelevance.toFixed(3)}</span> (from {relevanceMetrics.totalComments} comments).</li>
                    <li><strong>effective_sample</strong>: Sum of all relevance_scores. For this game: <span className="text-orange-400 font-semibold">{relevanceMetrics.effectiveSample.toFixed(1)}</span> (from {relevanceMetrics.totalComments} comments). A comment with relevance_score=1.0 contributes 1.0, while relevance_score=0.5 contributes only 0.5.</li>
                    <li><strong>avg_upvotes</strong>: Average number of upvotes per comment. For this game: <span className="text-orange-400 font-semibold">{relevanceMetrics.avgUpvotes.toFixed(1)}</span> upvotes/comment (from {relevanceMetrics.totalComments} comments). Engagement quality = {Math.min(1.0, relevanceMetrics.avgUpvotes / 10.0).toFixed(2)} (10+ upvotes = high quality).</li>
                  </ul>
                </div>
                {predictionData.metrics?.composite && (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <span className="text-gray-400 text-sm">Home Composite:</span>
                      <div className="text-xl font-bold text-orange-400">{predictionData.metrics.composite.home.toFixed(4)}</div>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Away Composite:</span>
                      <div className="text-xl font-bold text-orange-400">{predictionData.metrics.composite.away.toFixed(4)}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Prediction Parameters Display */}
            {predictionParams && (
              <div className="mt-4">
                <PredictionParameters
                  parameters={predictionParams.parameters}
                  isOptimized={predictionParams.isOptimized}
                />
              </div>
            )}
          </div>

          {/* Stage 5: Prediction */}
          <div className="card">
            <div className="flex items-center mb-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-600 text-white font-bold text-lg mr-4">
                5
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-white flex items-center">
                <HiCursorArrowRays className="h-6 w-6 mr-2 text-orange-600" />
                Stage 5: Prediction
              </h2>
            </div>
            <div className="space-y-3 text-gray-300">
              <div>
                <p className="font-medium mb-1">What:</p>
                <p className="text-sm">Composite scores are compared to determine which fanbase is more optimistic about their team's chances.</p>
              </div>
              <div>
                <p className="font-medium mb-1">Why:</p>
                <p className="text-sm">The final prediction indicates which fanbase has higher confidence, not the actual match outcome.</p>
              </div>
              <div className="bg-gray-800 p-4 rounded-lg mt-4">
                <p className="text-sm font-medium mb-2">Formula:</p>
                <pre className="text-xs bg-gray-900 p-3 rounded border border-gray-700 overflow-x-auto"><code>{`comp_delta = home_composite - away_composite
if |comp_delta| < ${predictionParams?.parameters?.draw_threshold ?? 0.03} → DRAW
elif comp_delta > 0 → HOME
else → AWAY`}</code></pre>
              </div>
              <div className="bg-gray-800 p-4 rounded-lg mt-4">
                <p className="text-sm font-medium mb-2">Prediction Strength:</p>
                <pre className="text-xs bg-gray-900 p-3 rounded border border-gray-700 overflow-x-auto"><code>{`composite_diff = |home_composite - away_composite|
≥ 1.00 VERY_STRONG, ≥ 0.75 STRONG, ≥ 0.50 MODERATE, ≥ 0.25 WEAK, else VERY_WEAK`}</code></pre>
              </div>
              <div className="mt-6 text-center">
                <div className="text-lg font-medium text-gray-300 mb-2">Predicted Winner:</div>
                <div className={`inline-flex items-center px-6 py-3 rounded-full text-lg font-medium ${
                  predictionData.predicted_winner === 'HOME' ? 'bg-orange-100 text-orange-800 border-2 border-orange-300' :
                  predictionData.predicted_winner === 'AWAY' ? 'bg-orange-100 text-orange-800 border-2 border-orange-300' :
                  'bg-gray-100 text-gray-800 border-2 border-gray-300'
                }`}>
                  {predictionData.predicted_winner === 'HOME' ? predictionData.home_team :
                   predictionData.predicted_winner === 'AWAY' ? predictionData.away_team :
                   'Draw'}
                </div>
                <div className="mt-3 text-sm text-gray-400">
                  <strong>Strength:</strong> {predictionData.prediction_strength}
                </div>
              </div>
            </div>
          </div>

          {/* Stage 6: Reliability Assessment */}
          <div className="card">
            <div className="flex items-center mb-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-600 text-white font-bold text-lg mr-4">
                6
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-white flex items-center">
                <HiShieldCheck className="h-6 w-6 mr-2 text-orange-600" />
                Stage 6: Reliability Assessment
              </h2>
            </div>
            <div className="space-y-3 text-gray-300">
              <div>
                <p className="font-medium mb-1">What:</p>
                <p className="text-sm">Data quality and sample size are evaluated to determine confidence level in the prediction.</p>
              </div>
              <div>
                <p className="font-medium mb-1">Why:</p>
                <p className="text-sm">Not all predictions are equally reliable - predictions with more data and higher quality are more trustworthy.</p>
              </div>
              <div className="bg-gray-800 p-4 rounded-lg mt-4">
                <p className="text-sm font-medium mb-2">Reliability Calculation:</p>
                <pre className="text-xs bg-gray-900 p-3 rounded border border-gray-700 overflow-x-auto"><code>{`sample_score   = log(sample_size + 1) / log(1001)
data_quality  = (avg_relevance × 0.7) + (filter_ratio × 0.3)
recency_score = recent_count / total_count
engagement    = min(1.0, avg_upvotes / 10.0)
source_diversity = 1.0 (Reddit-only)
balance_score = 1.0 - (|home_count - away_count| / total_count)
overall_conf = balance_score × (0.4×sample_score + 0.25×data_quality + 0.15×recency + 0.1×engagement + 0.1×source_diversity)`}</code></pre>
                <pre className="text-xs bg-gray-900 p-3 rounded border border-gray-700 overflow-x-auto mt-2"><code>{`tiers: ≥0.80 VERY_HIGH, ≥0.65 HIGH, ≥0.45 MEDIUM, ≥0.25 LOW, else VERY_LOW`}</code></pre>
                <div className="mt-3 p-3 bg-gray-900 rounded border border-gray-700">
                  <p className="text-xs font-medium mb-2 text-gray-300">Key Definitions:</p>
                  <ul className="text-xs text-gray-400 space-y-1 list-disc list-inside">
                    <li><strong>avg_relevance</strong>: Average of all relevance_scores for this game: <span className="text-orange-400 font-semibold">{relevanceMetrics.avgRelevance.toFixed(3)}</span> (see Stage 4.3 for definition).</li>
                    <li><strong>avg_upvotes</strong>: Average number of upvotes per comment for this game: <span className="text-orange-400 font-semibold">{relevanceMetrics.avgUpvotes.toFixed(1)}</span> upvotes/comment (from {relevanceMetrics.totalComments} comments). Calculated as sum(upvotes) / len(comments).</li>
                    <li><strong>balance_score</strong>: Penalizes imbalanced samples. Perfect balance (50/50) = 1.0. For this game: {predictionData?.fan_breakdown ? (
                      (() => {
                        const homeCount = predictionData.fan_breakdown.home_fans
                        const awayCount = predictionData.fan_breakdown.away_fans
                        const total = homeCount + awayCount
                        const imbalance = Math.abs(homeCount - awayCount)
                        const balanceScore = total > 0 ? 1.0 - (imbalance / total) : 0.0
                        return <><span className="text-orange-400 font-semibold">{balanceScore.toFixed(3)}</span> (home: {homeCount}, away: {awayCount})</>
                      })()
                    ) : 'N/A'}.</li>
                  </ul>
                </div>
              </div>
              {predictionData.reliability_metrics && (
                <div className="mt-6">
                  <ConfidenceIndicator
                    confidence={predictionData.reliability_metrics.overall_confidence}
                    reliability={predictionData.reliability_metrics.overall_reliability}
                    sampleSize={predictionData.data_sources.total_sentiment_data}
                    dataSources={predictionData.reliability_metrics.data_quality_breakdown}
                    className="max-w-md mx-auto"
                  />
          </div>
        )}
      </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default LiveGamePage
